const craft = [
  {
    slug: "custom-applications",
    code: "01",
    title: "Custom Applications",
    short:
      "Web and back-office systems built around the operations they support — typed, tested, documented.",
    body:
      "Most operational software fails not because the chosen platform was wrong, but because the operation did not fit the platform's assumptions. We design and build custom applications around the process the business actually runs — order management, dispatch, fulfilment, claims, back-office workflows, internal tooling — typed end-to-end, tested at every layer, documented in writing so the next engineer onto the codebase can be productive in a week.",
    deliverables: [
      "Discovery, written specification and architecture document",
      "Typed, tested production code in the operator's repository",
      "Operational runbook and on-call documentation",
      "Deployment pipeline and observability shipped on first release",
      "Knowledge-transfer programme with named internal engineers",
    ],
    stack: ["TypeScript", "Node.js", "Next.js", "PostgreSQL", "Python / FastAPI", "Go"],
  },
  {
    slug: "mobile-engineering",
    code: "02",
    title: "Mobile Engineering",
    short:
      "iOS, Android and cross-platform builds — built native where it matters, cross-platform where it doesn't.",
    body:
      "Mobile is the surface where users decide whether the system feels considered or careless. We engineer mobile applications natively when performance, hardware integration or platform conventions justify it, and cross-platform when the work is parity feature delivery — without pretending the choice does not have consequences. Every release ships with crash reporting, performance telemetry and a written rollback procedure.",
    deliverables: [
      "Native iOS (Swift / SwiftUI) and Android (Kotlin / Compose) where required",
      "Cross-platform (React Native / Flutter) for parity-feature applications",
      "App Store and Play Store release engineering and review handling",
      "Crash and performance telemetry with named owners",
      "Accessibility audited to platform-level WCAG-AA conformance",
    ],
    stack: ["Swift", "Kotlin", "React Native", "Flutter", "Sentry", "Firebase"],
  },
  {
    slug: "system-integration",
    code: "03",
    title: "System Integration",
    short:
      "ERP/CRM connectors, ETL, event-driven middleware, and the API design that holds them together.",
    body:
      "Most enterprise systems are not single applications — they are a constellation of platforms held together by integration that someone wrote in a hurry. We design and build the integration layer as a first-class system: explicit contracts, idempotent operations, retry semantics that an auditor can defend, and the observability to prove every message was either delivered or accounted for. SAP, Microsoft Dynamics, Salesforce, NetSuite, the long tail of internal systems — connected reliably.",
    deliverables: [
      "Integration architecture with explicit data ownership boundaries",
      "ETL and event-driven pipelines with idempotency and replay",
      "REST and event-based API design with versioning policy",
      "Connectors for SAP, Dynamics, Salesforce, NetSuite, internal systems",
      "End-to-end tracing across systems with delivery accounting",
    ],
    stack: ["Kafka", "RabbitMQ", "Airflow", "OpenAPI", "GraphQL", "OAuth2 / OIDC"],
  },
  {
    slug: "cloud-devops",
    code: "04",
    title: "Cloud & DevOps",
    short:
      "AWS and Azure architectures, deployment pipelines, observability and the cost discipline to operate them.",
    body:
      "Cloud architecture is not a diagram — it is a monthly bill, an incident response posture and a backup that has been restored at least once. We design cloud platforms around the operations team that will own them, ship infrastructure-as-code, instrument observability before the first release, and write the cost dashboards that finance signs off on. The platform we hand over is one the operator can run without us — that is the point.",
    deliverables: [
      "Cloud landing zone with policy guardrails and naming convention",
      "Infrastructure-as-code (Terraform / Pulumi) for the full environment",
      "CI/CD pipelines with automated tests and rollback procedure",
      "Observability — logs, metrics, traces — with named alert ownership",
      "Cost reporting and committed-use planning written for finance",
    ],
    stack: ["AWS", "Azure", "Terraform", "Kubernetes", "Datadog", "Grafana / Loki"],
  },
  {
    slug: "product-audit",
    code: "05",
    title: "Product Audit & Architecture",
    short:
      "Independent technical due diligence, second-opinion architecture review, and the rebuild roadmap when it is needed.",
    body:
      "Sometimes the operator does not need new code — they need a defensible answer to whether the current code can carry the next three years. We run independent technical audits: architecture review, code-quality assessment, operations posture, security baseline, dependency exposure, and the realistic remediation plan. The output is a written report addressed to the board, not a presentation aimed at securing follow-on work.",
    deliverables: [
      "Architecture review with defensible written assessment",
      "Code-quality, test-coverage and dependency-exposure audit",
      "Operations and incident-response posture assessment",
      "Security baseline review aligned to sector regulation",
      "Realistic remediation roadmap with cost and time-to-value",
    ],
    stack: ["SonarQube", "Snyk", "Renovate", "Semgrep", "OpenTelemetry"],
  },
];

export default craft;
